import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, Briefcase, FileWarning, History, ChevronDown, ChevronUp, CheckCircle2, XCircle, Clock3 } from "lucide-react";
import { yearLabel } from "@/components/shared";

function StatusBadge({ status, daysLate }) {
  if (status === "on_time") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600 whitespace-nowrap">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> À temps
      </span>
    );
  }
  if (status === "late") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-600 whitespace-nowrap">
        <Clock3 className="h-3.5 w-3.5 shrink-0" /> {daysLate} jour{daysLate > 1 ? "s" : ""} de retard
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-600 whitespace-nowrap">
      <XCircle className="h-3.5 w-3.5 shrink-0" /> Jamais{daysLate != null ? ` (${daysLate} j)` : ""}
    </span>
  );
}

export default function AlertsPanel() {
  const [data, setData] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(false);

  useEffect(() => {
    api.get("/admin/pfmp-alerts").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  const toggleHistory = () => {
    if (!showHistory && history === null && !historyError) {
      setHistoryLoading(true);
      api.get("/admin/pfmp-alerts/history")
        .then(({ data }) => setHistory(data.history))
        .catch(() => setHistoryError(true))
        .finally(() => setHistoryLoading(false));
    }
    setShowHistory((v) => !v);
  };

  if (!data) return null;
  const { empty_portfolio: emptyPortfolio, missing_career_docs: missingDocs } = data;

  return (
    <div className="rounded-3xl border border-rose-200 bg-rose-50/60 shadow-sm p-5 mb-6 mt-8" data-testid="alerts-panel">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="h-9 w-9 rounded-2xl bg-rose-600 text-white flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5" /></div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-lg font-semibold text-slate-900 leading-tight">Alertes PFMP</p>
          <p className="text-sm text-slate-500 font-medium">À suivre avec les élèves concernés</p>
        </div>
        <button
          type="button"
          onClick={toggleHistory}
          data-testid="alerts-history-toggle"
          className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-rose-600 hover:text-rose-700 bg-white border border-rose-200 rounded-full px-3 py-1.5 transition-colors"
        >
          <History className="h-3.5 w-3.5" /> Historique
          {showHistory ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {emptyPortfolio.length === 0 && missingDocs.length === 0 && (
        <p className="text-sm text-slate-500 font-medium mb-1" data-testid="alerts-none-current">Aucune alerte en cours.</p>
      )}

      {emptyPortfolio.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-rose-500 mb-1.5 flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5" /> Portfolio PFMP non rempli
          </p>
          <div className="space-y-1.5">
            {emptyPortfolio.map((s) => (
              <div key={s.id} data-testid={`alert-empty-portfolio-${s.id}`} className="flex items-center gap-3 bg-white border border-rose-100 rounded-2xl px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 truncate">{s.name} <span className="text-xs text-slate-400 font-semibold">· {yearLabel(s.year)}</span></p>
                  <p className="text-xs text-slate-400 font-semibold truncate">{s.pfmp_title}</p>
                </div>
                <span className="text-xs font-bold text-rose-600 whitespace-nowrap">
                  {s.days_since_end === 0 ? "Terminée aujourd'hui" : `Terminée depuis ${s.days_since_end} jour${s.days_since_end > 1 ? "s" : ""}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {missingDocs.length > 0 && (
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-rose-500 mb-1.5 flex items-center gap-1.5">
            <FileWarning className="h-3.5 w-3.5" /> CV / lettre de motivation manquants
          </p>
          <div className="space-y-1.5">
            {missingDocs.map((s) => (
              <div key={s.id} data-testid={`alert-missing-docs-${s.id}`} className="flex items-center gap-3 bg-white border border-rose-100 rounded-2xl px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-slate-800 truncate">{s.name} <span className="text-xs text-slate-400 font-semibold">· {yearLabel(s.year)}</span></p>
                  <p className="text-xs text-slate-400 font-semibold truncate">{s.missing.join(" et ")} · avant {s.pfmp_title}</p>
                </div>
                <span className="text-xs font-bold text-rose-600 whitespace-nowrap">
                  {s.days_until_start === 0 ? "PFMP aujourd'hui" : `PFMP dans ${s.days_until_start} jour${s.days_until_start > 1 ? "s" : ""}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showHistory && (
        <div className="mt-4 pt-4 border-t border-rose-200" data-testid="alerts-history-section">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
            <History className="h-3.5 w-3.5" /> Historique des résolutions
          </p>
          {historyLoading && (
            <p className="text-sm text-slate-400 font-medium" data-testid="alerts-history-loading">Chargement…</p>
          )}
          {!historyLoading && historyError && (
            <p className="text-sm text-rose-500 font-medium" data-testid="alerts-history-error">
              Impossible de charger l'historique pour le moment. Réessaie plus tard.
            </p>
          )}
          {!historyLoading && !historyError && history && history.length === 0 && (
            <p className="text-sm text-slate-400 font-medium" data-testid="alerts-history-empty">
              Pas encore de PFMP terminée : l'historique apparaîtra une fois les premières PFMP passées.
            </p>
          )}
          {!historyLoading && history && history.length > 0 && (
            <div className="space-y-2">
              {history.map((s) => (
                <div key={s.id} data-testid={`alerts-history-student-${s.id}`} className="bg-white border border-slate-200 rounded-2xl px-4 py-3">
                  <p className="font-bold text-slate-800 mb-2">{s.name} <span className="text-xs text-slate-400 font-semibold">· {yearLabel(s.year)}</span></p>
                  <div className="space-y-1">
                    {s.portfolios.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between gap-3">
                        <span className="text-xs text-slate-500 font-semibold truncate">Portfolio · {p.pfmp_title}</span>
                        <StatusBadge status={p.status} daysLate={p.days_late} />
                      </div>
                    ))}
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-500 font-semibold truncate">CV</span>
                      <StatusBadge status={s.cv.status} daysLate={s.cv.days_late} />
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs text-slate-500 font-semibold truncate">Lettre de motivation</span>
                      <StatusBadge status={s.cover_letter.status} daysLate={s.cover_letter.days_late} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
